import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { Add16, Subtract16 } from '@carbon/icons-react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import React, { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import ColorIcon from '../colorIcon';

export interface ColorListProps {
  colors: SchemaTypes.InteriorColor[];
  onChange: (filterOptions: string[]) => void;
  hasShowMore: boolean;
  selected: string[];
}

// eslint-disable-next-line react/display-name
const ColorList: FC<ColorListProps> = (props) => {
  const { colors, onChange, selected = [] } = props;
  const [expanded, setExpanded] = useState(false);

  const hasShowMore = colors.length > 4;

  const className = {
    container: clsx('h-full max-w-[500px]'),
    highlighted: clsx('gap-20px grid grid-cols-3 grid-flow-row xl:grid-cols-4'),
    extra: clsx('ease-in-out gap-20px grid grid-cols-3 xl:grid-cols-4'),
    expandText: clsx(
      'inline-block leading-5 ml-[9px] text-p1 text-white underline'
    ),
    iconStyle: clsx('inline-block text-white'),
  };
  const { container, highlighted, extra, expandText, iconStyle } = className;
  const intl = useIntl();
  const colorListRef = useRef<HTMLDivElement>(null);

  const onColorClick = (name: string, added: boolean) => {
    if (added) {
      onChange([...selected, name]);
    } else {
      onChange(selected.filter((e) => e !== name));
    }
  };

  const renderHighlighted = () =>
    colors?.map(
      (color: any, index: number) =>
        color &&
        color.highlighted && (
          <ColorIcon
            key={`${color.displayName}-${index}`}
            hex={color.hex}
            onClick={onColorClick}
            displayName={color.displayName}
            isDefaultActive={
              selected.indexOf(color.name) !== -1 || color.isDefaultActive
            }
            name={color.name}
          />
        )
    );
  const renderExtraColors = () =>
    colors?.map(
      (color: any, index: number) =>
        color &&
        !color.highlighted && (
          <ColorIcon
            key={`${color.displayName}-${index}`}
            hex={color.hex}
            onClick={onColorClick}
            displayName={color.displayName}
            isDefaultActive={
              selected.indexOf(color.name) !== -1 || color.isDefaultActive
            }
            name={color.name}
          />
        )
    );
  return (
    <div className={container}>
      <div className={highlighted}>{renderHighlighted()}</div>
      <Transition
        as={'div'}
        show={expanded}
        enter="transition-[max-height] duration-700 ease-out"
        enterFrom="!max-h-0 overflow-hidden"
        enterTo="overflow-hidden"
        leave="transition-[max-height] duration-700 ease-out"
        leaveFrom="overflow-hidden"
        leaveTo="!max-h-0 overflow-hidden"
        beforeEnter={() => {
          const elm = colorListRef.current;
          if (elm && elm.parentElement) {
            elm.parentElement.style.maxHeight = `${elm.clientHeight}px`;
          }
        }}
      >
        <div className={extra} ref={colorListRef}>
          {renderExtraColors()}
        </div>
      </Transition>
      {hasShowMore && (
        <div className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
          {expanded && <Subtract16 className={iconStyle} />}
          {!expanded && <Add16 className={iconStyle} />}
          <span className={expandText}>
            {expanded
              ? translate(intl, 'ui.EXTERIOR_COLOR_LIST.showLess')
              : translate(intl, 'ui.EXTERIOR_COLOR_LIST.showMore')}
          </span>
        </div>
      )}
    </div>
  );
};

export default ColorList;
